import type { FieldKind } from "@productlane/lib/src/tiptap/extensions/mintlify/fields-html"
import {
	DEFAULT_EXPANDABLE_TITLE,
	expandableHtml,
	fieldAttributesFromJsx,
	fieldHtml,
	findTagBlocks,
	jsxBoolean,
	jsxText,
	parseJsxAttributes,
} from "@productlane/lib/src/tiptap/extensions/mintlify/fields-html"

import type { MdxBlockReader } from "./registry"

/**
 * A regex can't count nesting, and a ResponseField inside an Expandable inside
 * a ResponseField is the normal shape here. `matchAll` asks the pattern for
 * its matches, so this one hands back the outermost balanced blocks.
 * Groups: 1 tag, 2 attributes, 3 body.
 */
class TagBlockPattern extends RegExp {
	constructor(private readonly tags: string[]) {
		super(`<(${tags.join("|")})\\b`, "g")
	}

	override *[Symbol.matchAll](
		source: string,
	): RegExpStringIterator<RegExpMatchArray> {
		for (const block of findTagBlocks(source, this.tags)) {
			const match = [
				source.slice(block.start, block.end),
				block.tag,
				block.attributes,
				block.body,
			] as RegExpMatchArray
			match.index = block.start
			match.input = source
			yield match
		}
	}
}

const FIELD_KIND: Record<string, FieldKind> = {
	ParamField: "param",
	ResponseField: "response",
}

export const fieldsReaders: MdxBlockReader[] = [
	{
		name: "field",
		pattern: new TagBlockPattern(Object.keys(FIELD_KIND)),
		render: (match, parseBody) => {
			const kind = FIELD_KIND[match[1] ?? ""] ?? "response"
			const attrs = fieldAttributesFromJsx(
				kind,
				parseJsxAttributes(match[2] ?? ""),
			)
			const body = match[3] ?? ""
			return fieldHtml(kind, attrs, body.trim() ? parseBody(body) : "")
		},
	},
]

export const expandableReaders: MdxBlockReader[] = [
	{
		name: "expandable",
		pattern: new TagBlockPattern(["Expandable"]),
		render: (match, parseBody) => {
			const props = parseJsxAttributes(match[2] ?? "")
			const title = jsxText(props.title) ?? DEFAULT_EXPANDABLE_TITLE
			const body = match[3] ?? ""
			return expandableHtml(
				title,
				jsxBoolean(props.defaultOpen),
				body.trim() ? parseBody(body) : "",
			)
		},
	},
]
